import { useCallback, useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
// Distinct fill cues per platform so the operator can tell by ear
// which platform just filled without looking at the row. Each fill
// event plays the clip ONCE (no loop); rapid bursts are throttled by
// EXECUTION_CLIP_MS so we never restart a clip mid-play. When both
// platforms fill in the same cycle the clips overlap on independent
// players — the "both sides are moving right now" signal.
import kalshiFillSound from 'src/Sounds/kalshi-fill.mp3';
import polyFillSound from 'src/Sounds/poly-fill.mp3';
// Distinct alert tone for errors / manual-intervention events.
// Re-uses the existing doorbell clip — was the previous full-fill
// sound, freed up when fills migrated to the cash register loop.
import alarmSound from 'src/Sounds/error.mp3';
// `counter_stuck` gets its OWN clip so a naked leg is tellable by ear from
// every other alarm — it is the one alarm that means "money is un-hedged
// right now", and the operator asked to stop guessing which of the four it
// was. Every other alarm keeps the shared doorbell.
import counterStuckSound from 'src/Sounds/counter_stuck.mp3';
import type {
  NotificationEvent,
  NotificationEventType
} from '../types/notifications.types';

/**
 * Two audio tiers for prediction-market notifications.
 *
 *   1. **Alarm** — a four-type whitelist (`ALARM_EVENT_TYPES` below), not
 *      "every error". There is NO conditional path into this tier any
 *      more: membership in the Set is the whole rule. Plays a doorbell
 *      tone (`counter_stuck` has its own clip AND its own playback lane
 *      — see `DEDICATED_ALARM_TYPE`),
 *      throttled by ALARM_COOLDOWN_MS so a flurry of related events
 *      doesn't blast the operator — read that constant's known limit
 *      before treating the tone as a count. Severity does NOT decide the
 *      tier: `counter_stuck` is a wire `warning` and rings; several wire
 *      `error`s do not.
 *
 *   2. **Execution** — every fill, partial fill, and cycle-complete
 *      event, regardless of origin: auto-unwinder, auto-opener
 *      (direction === 'entry'), AND manual operator orders. SPLIT BY
 *      PLATFORM:
 *      Kalshi fills play `kalshi-fill.mp3` on a dedicated burst-
 *      coalesced loop, Polymarket fills play `poly-fill.mp3` on a
 *      separate dedicated burst-coalesced loop. The two loops are
 *      independent and can overlap — when both platforms fill in
 *      the same cycle the operator hears both rhythms, which is the
 *      exact "both sides are moving" signal we want.
 *
 *      Manual fills (`manual_order_executed`) ride this same
 *      per-platform cue rather than a separate achievement chime — the
 *      operator wanted ONE consistent "platform X just filled" sound
 *      across every fill source, automation or manual. Per-chunk
 *      partials on a still-resting manual order
 *      (`manual_order_partial_fill`) ride the SAME cue too; the 🟡 chip
 *      is the visual differentiator, audio stays consistent so a
 *      partial chunk isn't silently dropped.
 *
 * Priority order (top wins): **alarm > execution**. Alarms punch
 * through a running fill loop as the only "stop and look" signal.
 *
 * Anything not in any set stays silent — the row, color, and Slack
 * post are still there, just no sound. Avoids alarm fatigue on
 * lifecycle / digest / recovery-success events. `tna_negative` is
 * deliberately silent too (sound removed 2026-06-09 by operator
 * request) — the rows + Slack post still surface every crossing.
 *
 * The alarm tier is the ONLY thing this hook makes audible besides
 * fills. Everything else the operator hears comes from page-scoped
 * hooks on a different socket namespace and is untouched by this file:
 * `useSniperReversalSound` (goal reversal, Sniper page),
 * `useVarReviewSound` (VAR started) and `useMatchEventSound`
 * (goal / corner). Silencing something here never silences those.
 */
// Fills route to the per-platform cash-register loop regardless of
// origin: auto-unwinder, auto-opener (direction === 'entry'), AND
// manual operator orders. The operator wants ONE consistent "platform X just filled"
// audio signal — no separate chime for manual fills, no silence for
// opener fills. `manual_order_placed` (the click ack) stays unlisted
// because the click itself already confirmed it.
// Base event types only — the entry-vs-unwind axis now lives on
// `event.direction`, not the type name, so a single base entry covers
// both the opener (buy-side) and unwinder (sell-side) fill of the same
// kind. The cue is identical for both directions anyway (the operator
// wants ONE "platform X just filled" signal regardless of side).
const EXECUTION_EVENT_TYPES = new Set<NotificationEventType>([
  // Full fills
  'maker_filled',
  'counter_filled',
  'recovery_filled',
  'crossing_executed',
  // Partial fills
  'maker_partial_fill',
  'partial_fill_accumulated',
  'crossing_partial',
  'crossing_accumulated',
  // Cycle done
  'execution_complete',
  // Manual operator-placed fill — operator wanted the same per-platform
  // fill sound as automation fills, no separate chime. Partial chunks on
  // a still-resting manual order ring the same per-platform cue as the
  // full fill — the 🟡 chip is the only thing that distinguishes them
  // visually, and the operator wants identical audio so a per-trade
  // partial isn't silent.
  'manual_order_executed',
  'manual_order_partial_fill',
  // Sniper single-FAK sweep landed — ride the same per-platform fill cue so
  // the operator hears "venue X just filled" on a live-event snipe too. The
  // auto-NO period-close tail is the same kind of event (a real GTC fill on the
  // NO side), so it rides the SAME cue — the operator must hear it fill too.
  'sniper_fired',
  'sniper_auto_no_fired',
  // A VAR all-clear rebuy — the goal was confirmed and we bought the position
  // back. A BUY fill, so it rides the execution cue (and is audibly distinct from
  // the annulment SELL exit, which alarms below).
  'sniper_var_rebuy',
  // The ladder's resting exit FILLING is a real fill — it rides the same cue.
  // Its placement/amend do not: on a rising confidence they fire repeatedly,
  // and a sound on every rung would train the operator to ignore the cue.
  'sniper_ladder_sell_filled',
  // Every booked BUY fill delta — partial chunks included. Same rationale as
  // `manual_order_partial_fill`: the operator hears "venue X just filled" for
  // each chunk; the filled-of-total on the row is the visual differentiator.
  'sniper_buy_filled'
]);

/**
 * The alarm tone is a DELIBERATELY SHORT whitelist — four event types,
 * chosen by the operator (narrowed to five 2026-08-07, to four
 * 2026-08-14). Everything else that used to ring (`counter_failed`,
 * `out_of_balance`, `over_hedged`, `counter_unconfirmed`,
 * `poly_cancel_abandoned`, and — 2026-08-14 — `rule_state_stale` and
 * `recovery_filled` with a `failed`/`no_bid` counter) is now SILENT:
 * still a row, still a colour, still a Slack post, just no doorbell.
 * None of them loses its last alerting channel by going quiet here.
 *
 * The two 2026-08-14 removals are worth their own line, because both
 * look like obvious ring candidates:
 *   - `rule_state_stale` (the 🛑 "halted" card) is a STATE, read off the
 *     page and the feed rather than reacted to in the moment, and it
 *     fires often enough that ringing it eroded the tone for the alarms
 *     that ARE about money moving.
 *   - `recovery_filled` with `failed`/`no_bid` went silent rather than
 *     down to the fill cue: nothing filled, so the fill cue would say
 *     the opposite of what happened (see `classifyEvent`).
 *
 * Two of the removals were never live to begin with, and the reasons
 * are worth keeping so nobody re-adds them expecting a ring:
 * `platform_disconnect` is routed `['slack']` ONLY (notifications-core
 * `routing.ts`) so it never reaches this hook at all; and the three
 * `sniper_*` types (`sniper_fire_blocked`, `sniper_var_reversal`,
 * `sniper_goal_cancelled`) travel on the `/match-events` namespace, not
 * the root `notifications:stream` that feeds this hook — the audible
 * cue on the Sniper page is `useSniperReversalSound` and it is
 * unaffected by anything in this file.
 *
 * The reasoning is alarm fatigue, and it is the whole point of this
 * set. A tone that fires on a dozen different conditions stops being a
 * "stop and look" signal and becomes background noise — at which point
 * the ONE event that genuinely needed eyes is the one that gets
 * ignored. Widening this set back out is not a small change; it costs
 * the meaning of every entry already in it.
 *
 * `emergency_stop` is not here either: panic is operator-initiated, so
 * the doorbell would just confirm a button the operator just pressed.
 */
export const ALARM_EVENT_TYPES = new Set<NotificationEventType>([
  // A generic engine fault — nothing else describes it, so it rings.
  'error',
  // The account-wide watcher's naked-leg net: an automation order filled
  // with NO owning rule / live engine — money moved and nobody is hedging
  // it. Nothing else alerts (scope-aware engines demote the fill), so the
  // operator MUST hear this one.
  'orphaned_automation_fill',
  // A hedge leg resting unfilled while the first leg is already filled —
  // contracts are un-hedged RIGHT NOW. Severity on the wire is `warning`
  // (the engine left the order in place on purpose, it is not a fault),
  // but the money exposure is real and in the terminal case — unhedged
  // quantity AND no `counterStuckMarketHedgeSeconds` window — this card is
  // the LAST thing that will ever be said about the naked leg. That is
  // exactly why it rings despite not being an error: the tone, not the
  // chip colour, is what surfaces it. (2026-08-05 Aarhus/Sabah: 100
  // contracts left naked behind a silent warning.) The ONLY alarm with
  // its own clip and its own lane — see DEDICATED_ALARM_TYPE.
  'counter_stuck',
  // A leg hit its max-volume ceiling and stopped operating. The engine is
  // done with it until the operator raises the cap or lets it go, so this
  // one needs ears, not just a row.
  'max_volume_reached',
]);

/**
 * The ONE alarm that gets its own clip AND its own playback lane —
 * everything else in the whitelist shares the doorbell on the main lane.
 *
 * A lane is a `useAudioPlayer()` instance plus its own cooldown timestamp
 * and stop timer, exactly like the per-platform execution lanes below.
 * `counter_stuck` needs one because a distinct clip is worthless if the
 * shared cooldown can swallow it: the lanes are independent, so a
 * doorbell alarm in the same batch can no longer absorb the naked-leg
 * cue. Both may be audible at once, which is the correct signal — the
 * operator must hear the naked leg regardless of what else went wrong.
 *
 * Deliberately singular, not a map. One dedicated clip earns its keep by
 * standing out against a single background tone; a second would be a
 * vocabulary to memorise, and it could not silently join anyway — a new
 * lane means a new player, a new cooldown ref and a new timer, all wired
 * by hand right here.
 *
 * Clip must be shorter than ALARM_PLAY_MS or the stop timer truncates it
 * (`counter_stuck.mp3` ≈ 1.07 s, `error.mp3` ≈ 1.62 s, both well under).
 */
const DEDICATED_ALARM_TYPE: NotificationEventType = 'counter_stuck';
const DEDICATED_ALARM_SOUND = counterStuckSound;

/**
 * Per-platform "play in flight" window. Each fill event plays its
 * platform's clip ONCE (no loop) — subsequent fills arriving inside
 * this window are silently dropped so we don't restart the clip
 * mid-play and produce stutter. Tuned to a comfortable upper bound
 * over the actual clip durations measured with `afinfo`:
 *   - `kalshi-fill.mp3` ≈ 365 ms
 *   - `poly-fill.mp3`   ≈ 648 ms
 * 800 ms covers both with margin. If you swap a longer clip in,
 * bump this. If you swap a shorter clip in, leave it — the throttle
 * only suppresses extra plays, it never delays an idle one.
 *
 * Historical context: this used to be `TAIL_MS = 1500` with
 * `loop: true`, which played the OLD `cash-register.mp3` (~1 s) about
 * 1.5 times per fill — read as one continuous burst when fills
 * stacked. The new shorter clips made a single fill loop 3-4 times
 * (2026-05-23 bug). Switched to one-clip-per-fill + throttle.
 */
const EXECUTION_CLIP_MS = 800;

/**
 * Minimum gap between alarm sounds. Several errors firing within
 * this window play ONE alarm — the page row + Slack post still
 * surface every event, but the audio cue isn't repeated. Tuned
 * short enough that a genuine new error 5 s later still alerts.
 *
 * Counted PER LANE, so the two lanes never absorb each other. Within a
 * lane the tone still says "at least one", never "how many":
 * `PredictionNotificationSound` drains a whole socket batch inside the
 * same millisecond, so a goal on a busy event that puts 6 grouped rules
 * into `counter_stuck` in one engine tick plays the clip ONCE. The count
 * lives in the feed; the audio only says the lane fired.
 *
 * Splitting a lane per TYPE (rather than the current whitelist-wide
 * doorbell lane) would need a player, a timestamp and a stop timer each:
 * keying this window by type alone changes nothing audible, because a
 * second `load()` on one player destroys the howl still playing on it,
 * and this window and ALARM_PLAY_MS are both 3000 ms.
 */
const ALARM_COOLDOWN_MS = 3000;

/** How long an alarm sound plays before auto-stopping. */
const ALARM_PLAY_MS = 3000;

type Tier = 'execution' | 'alarm';
type ExecutionPlatform = 'kalshi' | 'polymarket';

/**
 * Surface an audio-load failure instead of swallowing it silently. The
 * old `catch {}` hid a broken `load(...)` so the operator lost the
 * audible fill/alarm cue with no signal at all. Every failure goes to
 * Sentry; the operator toast fires ONCE per session (`shownToast`) so a
 * persistently-broken audio context doesn't spam the UI.
 */
let audioLoadToastShown = false;
function reportSoundLoadFailure(err: unknown): void {
  Sentry.captureException(
    err instanceof Error ? err : new Error('notification sound load failed'),
    { tags: { area: 'notification-sound' } },
  );
  if (!audioLoadToastShown) {
    audioLoadToastShown = true;
    toast.error('Notification sounds unavailable — check audio permissions.', {
      toastId: 'notification-sound-load',
    });
  }
}

function classifyEvent(event: NotificationEvent): Tier | null {
  if (ALARM_EVENT_TYPES.has(event.type)) return 'alarm';
  if (event.type === 'recovery_filled') {
    // Recovery "executing" = automation is hedging the leg, no operator
    // action needed → execution tier (fill cue). Anything else ("failed"
    // / "no_bid") is SILENT: it rang until 2026-08-14 and the operator
    // dropped it, and the fill cue would be a lie — nothing filled. The
    // row, colour and Slack post still carry it.
    const counterAction = (event.payload as any)?.counterAction;
    return counterAction === 'executing' ? 'execution' : null;
  }
  if (EXECUTION_EVENT_TYPES.has(event.type)) return 'execution';
  return null;
}

/**
 * Resolve the platform an execution event is about, so we can route
 * to the right per-platform cash-register loop. Reads `event.platform`
 * first (set by the producer on every fill notification — see
 * `slack-notification.service.ts`'s `notifyMakerFilled` /
 * `notifyCounterFilled` / `notifyCrossingExecuted` etc.), and falls
 * back to payload fields for legacy events that didn't thread it
 * through. Returns `null` for events with no resolvable platform
 * (notably `execution_complete`) — the caller
 * extends whichever player is already running instead of starting
 * one arbitrarily, so a cycle-end summary on its own stays silent.
 */
function resolveExecutionPlatform(
  event: NotificationEvent
): ExecutionPlatform | null {
  const candidates: unknown[] = [
    event.platform,
    (event.payload as any)?.platform,
    (event.payload as any)?.counterPlatform,
    (event.payload as any)?.makerPlatform,
    // Sniper frames carry the target as `venue` (kalshi|polymarket).
    (event.payload as any)?.venue
  ];
  for (const c of candidates) {
    if (typeof c !== 'string') continue;
    const lc = c.toLowerCase();
    if (lc === 'kalshi' || lc === 'polymarket') return lc;
  }
  return null;
}

/**
 * Returns a STABLE `play(event)` — a per-platform fill clip for execution
 * events and a doorbell / naked-leg alarm for the whitelist. Designed
 * around the operator's reality: a quiet engine produces few sounds, a
 * busy cycle produces one clip per platform, and any alarm that genuinely
 * needs eyes punches through.
 *
 * IMPERATIVE ON PURPOSE. The caller drains a whole socket batch and calls
 * this once per event, in a plain loop. Feeding a batch through React
 * state instead LOSES EVENTS: `setState` per event coalesces into one
 * render, so only the last value of the batch survives — a `counter_stuck`
 * with anything after it in the same burst never rang at all, which no
 * amount of per-lane cooldown work can fix. Every dedupe/cooldown decision
 * lives inside this callback, so calling it in a tight loop is correct:
 * `lastPlayedId` still drops a repeat, and each lane still collapses its
 * own burst.
 */
export function useNotificationSoundPlayer(): (
  event: NotificationEvent
) => void {
  // Main player carries the shared doorbell; `counter_stuck` gets its
  // own (see DEDICATED_ALARM_TYPE) so the two alarm lanes can overlap
  // instead of absorbing each other. Execution gets its own
  // per-platform players below so a Kalshi fill loop and a Poly fill
  // loop can overlap without fighting over a single audio handle.
  //
  // One player per concurrent sound is not a style choice: a second
  // `load()` on a player destroys the howl currently attached to it
  // (`createHowl` → `destroyHowl` → `unload`), so two clips sharing a
  // player can never sound together.
  //
  // IMPORTANT: `useAudioPlayer()` returns a NEW wrapper object every
  // render (it spreads internal `state` into the return value, and
  // state updates fire on every audio event). The INNER `load`/`stop`
  // callbacks are stable (wrapped in `useCallback([])` inside the
  // lib), so we destructure them up front and use only those stable
  // refs in the effect dep arrays. Storing the wrapper objects
  // directly silently stopped every sound on each render because the
  // cleanup effect's dep changed each tick.
  const { load: loadMain, stop: stopMain } = useAudioPlayer();
  const { load: loadDedicated, stop: stopDedicated } = useAudioPlayer();
  const { load: loadKalshi, stop: stopKalshi } = useAudioPlayer();
  const { load: loadPoly, stop: stopPoly } = useAudioPlayer();
  const lastPlayedId = useRef<string | null>(null);

  // Set while EITHER alarm lane is playing, so an execution loop
  // doesn't clobber an in-progress alarm and a stale event re-render
  // doesn't restart an unrelated sound. The per-platform execution
  // players track their own burst state via the *StartedAt refs
  // below, so they don't need to read this.
  const currentTier = useRef<Tier | null>(null);

  // Per-platform "play in flight until" timestamp (Date.now() ms).
  // 0 = idle. Set on each load() to `now + EXECUTION_CLIP_MS` and
  // checked before starting a new play so rapid bursts don't restart
  // the clip mid-play (stutter). Reset to 0 by `stopAllExecution()`
  // when an alarm cuts the play short.
  const kalshiPlayingUntil = useRef(0);
  const polyPlayingUntil = useRef(0);

  // Alarm cooldown + stop timer, PER LANE. Keeping them separate is what
  // stops a doorbell alarm from swallowing a `counter_stuck` that lands
  // in the same drained batch.
  const lastAlarmAt = useRef(0);
  const lastDedicatedAlarmAt = useRef(0);
  const mainStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dedicatedStopTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const play = useCallback((event: NotificationEvent): void => {
    if (!event) return;
    if (event.id === lastPlayedId.current) return;
    const tier = classifyEvent(event);
    if (!tier) return;

    lastPlayedId.current = event.id;
    const now = Date.now();

    // Helpers — inlined so the callback's dep array stays accurate
    // (every player callback these read is one it depends on).
    const stopAllExecution = (): void => {
      kalshiPlayingUntil.current = 0;
      stopKalshi();
      polyPlayingUntil.current = 0;
      stopPoly();
    };

    // The tier is only idle once BOTH lanes are done — clearing it off
    // whichever timer happens to fire first would let a fill cue slip in
    // under an alarm still playing on the other lane.
    const clearTierIfNoLanePlaying = (): void => {
      if (!mainStopTimer.current && !dedicatedStopTimer.current) {
        currentTier.current = null;
      }
    };

    if (tier === 'alarm') {
      const dedicated = event.type === DEDICATED_ALARM_TYPE;
      const lastAt = dedicated ? lastDedicatedAlarmAt : lastAlarmAt;
      const stopTimer = dedicated ? dedicatedStopTimer : mainStopTimer;
      const load = dedicated ? loadDedicated : loadMain;
      const stop = dedicated ? stopDedicated : stopMain;

      // Cooldown is per lane, so this only ever silences a REPEAT of the
      // same lane — never the other alarm.
      if (now - lastAt.current < ALARM_COOLDOWN_MS) return;
      lastAt.current = now;

      // Interrupt any execution loop in progress — alarms have
      // priority. Operator should hear the doorbell over a running
      // fill loop.
      stopAllExecution();
      if (stopTimer.current) clearTimeout(stopTimer.current);
      currentTier.current = 'alarm';
      try {
        load(dedicated ? DEDICATED_ALARM_SOUND : alarmSound, {
          autoplay: true,
          loop: false,
        });
      } catch (err) {
        reportSoundLoadFailure(err);
        stopTimer.current = null;
        clearTierIfNoLanePlaying();
        return;
      }
      stopTimer.current = setTimeout(() => {
        stop();
        stopTimer.current = null;
        clearTierIfNoLanePlaying();
      }, ALARM_PLAY_MS);
      return;
    }

    // tier === 'execution' — defer only to alarms.
    if (currentTier.current === 'alarm') return;

    // One discrete play per fill event (no loop). The per-platform
    // `playingUntil` guard skips additional fills arriving while the
    // clip is still in flight, so we never restart the clip mid-play.
    // Inlined (rather than a separate helper) so the dep array stays
    // accurate — every player + ref read here is one this callback
    // already depends on.
    const playExecutionFill = (
      platform: ExecutionPlatform,
      tNow: number
    ): void => {
      const playingUntil =
        platform === 'kalshi' ? kalshiPlayingUntil : polyPlayingUntil;
      if (tNow < playingUntil.current) return;
      const load = platform === 'kalshi' ? loadKalshi : loadPoly;
      const src = platform === 'kalshi' ? kalshiFillSound : polyFillSound;
      try {
        load(src, { autoplay: true, loop: false });
      } catch (err) {
        reportSoundLoadFailure(err);
        return;
      }
      playingUntil.current = tNow + EXECUTION_CLIP_MS;
    };

    // Cycle-end summaries (`execution_complete`)
    // have no resolvable platform — the maker/counter fills inside
    // the cycle already triggered their own clip plays, so the
    // summary on its own stays silent.
    const platform = resolveExecutionPlatform(event);
    if (platform === null) return;

    playExecutionFill(platform, now);
  }, [
    loadMain,
    stopMain,
    loadDedicated,
    stopDedicated,
    loadKalshi,
    stopKalshi,
    loadPoly,
    stopPoly,
  ]);

  // Cleanup on unmount — stop every player so no audio handle
  // outlives the page navigation. Deps are the stable `stop`
  // callbacks (NOT the player wrapper objects) so this only
  // fires on unmount, not on every render.
  useEffect(() => {
    return () => {
      if (mainStopTimer.current) clearTimeout(mainStopTimer.current);
      if (dedicatedStopTimer.current) {
        clearTimeout(dedicatedStopTimer.current);
      }
      // Null the refs, not just the timeouts: a non-null timer ref means
      // "this lane is still playing" to `clearTierIfNoLanePlaying`, so
      // leaving them set would wedge `currentTier` at 'alarm' and mute
      // every fill for the rest of the session if this cleanup ever ran
      // with the component still alive.
      mainStopTimer.current = null;
      dedicatedStopTimer.current = null;
      currentTier.current = null;
      stopMain();
      stopDedicated();
      stopKalshi();
      stopPoly();
    };
  }, [stopMain, stopDedicated, stopKalshi, stopPoly]);

  return play;
}

/**
 * Thin single-event wrapper over {@link useNotificationSoundPlayer}, for
 * callers that hold ONE current event rather than draining a batch.
 *
 * ⚠️ Not for socket batches. React coalesces a run of `setState` calls
 * into one render, so feeding a batch through a state slot silently drops
 * every event but the last — use the imperative player and loop.
 *
 * Pass `null`/`undefined` for ticks where the event has already been
 * consumed. `play` guards on `event.id`, so an unrelated re-render does
 * not replay the cue.
 */
export function useNotificationSound(
  event: NotificationEvent | null | undefined
): void {
  const play = useNotificationSoundPlayer();
  useEffect(() => {
    if (event) play(event);
  }, [event, play]);
}
